import React from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { Text } from '@gluestack-ui/themed';
import type { TextStyle } from 'react-native';

/**
 * Detects if text contains LaTeX math notation.
 * Looks for patterns: \(...\) or \[...\]
 */
export function containsMath(text: string): boolean {
  if (!text) return false;
  return /\\\(.*?\\\)|\\\[.*?\\\]/s.test(text);
}

function normalizeMathInput(text: string): string {
  if (!text) return text;

  // Users sometimes paste JSON-escaped strings into inputs, e.g. "\\(\\int_0^1 x^2 \\, dx\\)".
  // Detect that style (double backslashes before delimiters or TeX commands) and unescape once.
  const looksDoubleEscaped = /\\\\\(|\\\\\[|\\\\[a-zA-Z]+/.test(text);
  if (!looksDoubleEscaped) return text;

  return text.replace(/\\\\/g, '\\');
}

export const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [['\\(', '\\)']],
    displayMath: [['\\[', '\\]']],
  },
  options: {
    enableMenu: false,
    menuOptions: {
      settings: {
        zoom: 'NoZoom',
        inTabOrder: false,
        enrich: false,
        assistiveMml: false,
        speech: false,
        braille: false,
        subtitles: false,
        viewBraille: false,
        help: false,
        infoType: false,
        infoRole: false,
        infoPrefix: false,
        backgroundOpacity: 0,
        foregroundOpacity: 1,
      },
    },
  },
};

interface MathRendererProviderProps {
  children: React.ReactNode;
}

export const MathRendererProvider: React.FC<MathRendererProviderProps> = ({ children }) => {
  const canRenderMathJax = typeof document !== 'undefined' && typeof window !== 'undefined';

  if (!canRenderMathJax) return children;

  return <MathJaxContext config={mathJaxConfig}>{children}</MathJaxContext>;
};

interface MathRendererProps {
  /**
   * Text content that may contain LaTeX notation in \(...\) or \[...\] delimiters
   */
  children: string;

  /**
   * Optional styles to apply to the text container
   */
  style?: TextStyle;
}

/**
 * MathRenderer component for rendering text with optional LaTeX math notation.
 *
 * Automatically detects LaTeX patterns (\(...\) for inline, \[...\] for display)
 * and renders them using MathJax. Plain text is rendered as-is.
 *
 * @example
 * ```tsx
 * <MathRenderer>
 *   The formula is \(E = mc^2\) which shows energy equivalence.
 * </MathRenderer>
 * ```
 *
 * @example
 * ```tsx
 * <MathRenderer>
 *   Display formula: \[ \int_0^\infty e^{-x^2} dx = \frac{\sqrt{\pi}}{2} \]
 * </MathRenderer>
 * ```
 */
export const MathRenderer: React.FC<MathRendererProps> = ({ children, style }) => {
  const canRenderMathJax = typeof document !== 'undefined' && typeof window !== 'undefined';
  const normalized = normalizeMathInput(children);

  // If no math detected, render plain text for better performance
  if (!canRenderMathJax || !containsMath(normalized)) {
    return <Text style={style}>{children}</Text>;
  }

  // Render with MathJax support
  return (
    <MathJax dynamic>
      <Text style={style}>{normalized}</Text>
    </MathJax>
  );
};

export default MathRenderer;
